/**
 * The served trial config (PPL-527) — `global_settings/trialConfig`, the same
 * document `entitlement/premium.ts` reads before it grants a trial at signup.
 *
 * The register page is the one surface that states the trial BEFORE anyone has
 * an account, so it cannot read the number off a user doc: `trialDays` is
 * config (1–90, an admin-written product number) and the page must never
 * hard-code 14 in a sentence it shows a stranger. `enabled` is the same master
 * switch that gates the grant, so when the trial is off the page says nothing
 * rather than promising something signup will not give.
 *
 * Pre-auth is fine: `global_settings/{document=**}` is `allow read: if true` in
 * the deployed ruleset (the public app-config space — version checks, feature
 * flags, promotions all live there), so a signed-out read is a normal read and
 * not a rules failure waiting to happen. `services/featureFlags` reads a
 * sibling document the same way.
 *
 * One read per session, module-cached exactly as `services/featureFlags` is:
 * every visitor to register would otherwise pay it on each mount, and the
 * fleet's Firestore read count is a live cost lever. A missing document, a
 * disabled trial, a malformed length and a failed read all resolve the same
 * way — `null`, meaning "say nothing" — so the caller has one condition and no
 * way to render a half-fact.
 *
 * Today the document does not exist in production at all (the trial has never
 * been switched on), so this hook resolves to `null` and the line it feeds does
 * not render. That is the intended state, not a bug: creating the document with
 * `enabled: true` is what publishes the sentence.
 *
 * Mirrors the app's `src/hooks/useTrialConfig.ts` (PPL-527, pplkpr#194) so the
 * two register screens cannot drift on when the line appears.
 */

import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { FIRESTORE_DB } from '../firebaseConfig';

export interface TrialConfig {
  /** Only ever `true` here — a disabled trial resolves to `null` instead. */
  enabled: true;
  /** Served length in days, validated against the server's own 1–90 range. */
  trialDays: number;
}

/** The server's guardrails (`entitlement/premium.ts`): outside these it is a typo. */
const MIN_TRIAL_DAYS = 1;
const MAX_TRIAL_DAYS = 90;

let configPromise: Promise<TrialConfig | null> | null = null;
let cached: TrialConfig | null = null;

/** `null` for anything we would not want to state: absent, off, or malformed. */
function parse(raw: Record<string, unknown> | undefined): TrialConfig | null {
  if (!raw || raw.enabled !== true) return null;
  const days = raw.trialDays;
  if (typeof days !== 'number' || !Number.isInteger(days)) return null;
  if (days < MIN_TRIAL_DAYS || days > MAX_TRIAL_DAYS) return null;
  return { enabled: true, trialDays: days };
}

function loadTrialConfig(): Promise<TrialConfig | null> {
  if (!configPromise) {
    configPromise = getDoc(doc(FIRESTORE_DB, 'global_settings', 'trialConfig'))
      .then((snap) => parse(snap.exists() ? (snap.data() as Record<string, unknown>) : undefined))
      .catch((err) => {
        console.warn('trialConfig: read failed, the trial line stays hidden', err);
        return null;
      })
      .then((config) => {
        cached = config;
        return config;
      });
  }
  return configPromise;
}

/**
 * The validated config, or `null` while the read is in flight and whenever
 * there is nothing truthful to say. Seeded from the session cache so a re-mount
 * does not flash the line in.
 */
export default function useTrialConfig(): TrialConfig | null {
  const [config, setConfig] = useState<TrialConfig | null>(cached);

  useEffect(() => {
    let alive = true;
    loadTrialConfig().then((c) => {
      if (alive) setConfig(c);
    });
    return () => {
      alive = false;
    };
  }, []);

  return config;
}
